/**
 * Voice AI — Agent Configuration Tab
 * Business hours and advanced settings only.
 * Basic info + personality + voice selection moved to agent-voice-tab.js.
 * Dispatches: save, delete, back, agent-changed
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';

export class AgentConfigTab extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
  };

  static styles = [designSystem, componentStyles, agentStyles];

  _toggleDay(day) {
    const agent = { ...this.agent };
    const hours = { ...agent.businessHours };
    hours[day] = { ...hours[day], enabled: !hours[day].enabled };
    agent.businessHours = hours;
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _sliderChanged(field, value) {
    const agent = { ...this.agent, [field]: value };
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _collectAndSave() {
    const root = this.shadowRoot;
    const field = (name) => {
      const el = root.querySelector(`[data-field="${name}"]`);
      return el ? el.value : '';
    };

    const updated = { ...this.agent };
    updated.isAgentOne = this.agent.isAgentOne;
    updated.guardrails = this.agent.guardrails;
    updated.transferNumber = field('transferNumber');
    updated.interruptionHandling = field('interruptionHandling');

    const rangeEl = root.querySelector('[data-field="maxDuration"]');
    if (rangeEl) updated.maxDuration = parseInt(rangeEl.value) || 600;

    const delayEl = root.querySelector('[data-field="answerDelay"]');
    if (delayEl) updated.answerDelay = parseInt(delayEl.value) || 0;

    // Business Hours
    const hoursRows = root.querySelectorAll('.hours-row');
    if (hoursRows.length > 0) {
      const bh = {};
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      hoursRows.forEach((row, i) => {
        const toggle = row.querySelector('.toggle');
        const times = row.querySelectorAll('.time-input');
        bh[days[i]] = {
          enabled: toggle ? toggle.classList.contains('active') : false,
          start: times[0] ? times[0].value : '',
          end: times[1] ? times[1].value : '',
        };
      });
      updated.businessHours = bh;
    }

    this.dispatchEvent(new CustomEvent('save', { detail: { agent: updated } }));
  }

  render() {
    const agent = this.agent;
    if (!agent) return html``;

    return html`
      ${iconStyles}

      <!-- Agent One Designation -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-solid fa-star" style="margin-right:var(--space-2);color:#C9A962;"></i>
          Agent One
        </div>
        <div class="config-section-desc">
          Designate this agent as the push-to-talk "Agent One" assistant. Only one agent can hold this role at a time.
          When enabled, a dedicated <strong>Agent One</strong> tab will appear with guardrail settings.
        </div>
        <div style="display:flex;align-items:center;gap:var(--space-3);margin-top:var(--space-3);">
          <div
            class="toggle ${agent.isAgentOne ? 'active' : ''}"
            @click=${() => {
              const updated = { ...this.agent, isAgentOne: !this.agent.isAgentOne };
              this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent: updated } }));
            }}
          ></div>
          <span style="font-size:var(--text-sm);color:var(--color-text-secondary);">
            ${agent.isAgentOne
              ? html`<span style="color:#2C6C11;font-weight:500;"><i class="fa-solid fa-circle-check" style="margin-right:4px;"></i>This agent is Agent One</span>`
              : 'Enable Agent One for this agent'}
          </span>
        </div>
      </div>

      <!-- Business Hours Section -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-clock" style="margin-right:var(--space-2);color:var(--color-warning);"></i>
          Business Hours
        </div>
        <div class="config-section-desc">Set when the agent is available to receive calls.</div>

        <div class="hours-grid">
          ${Object.entries(agent.businessHours || {}).map(([day, hours]) => html`
            <div class="hours-row">
              <div
                class="toggle ${hours.enabled ? 'active' : ''}"
                @click=${() => this._toggleDay(day)}
              ></div>
              <span class="hours-day">${day}</span>
              <input type="time" class="time-input" .value=${hours.start} ?disabled=${!hours.enabled} />
              <input type="time" class="time-input" .value=${hours.end} ?disabled=${!hours.enabled} />
            </div>
          `)}
        </div>
      </div>

      <!-- Advanced Section -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-sliders" style="margin-right:var(--space-2);color:var(--color-text-tertiary);"></i>
          Advanced
        </div>
        <div class="config-section-desc">Fine-tune agent behavior and limits.</div>

        <div class="form-grid full">
          <div class="form-group">
            <div class="slider-group">
              <div class="slider-header">
                <label class="form-label">Max Call Duration</label>
                <span class="slider-value">${Math.floor(agent.maxDuration / 60)}m ${agent.maxDuration % 60}s</span>
              </div>
              <input type="range" data-field="maxDuration" min="60" max="900" step="30" .value=${String(agent.maxDuration)} @input=${(e) => this._sliderChanged('maxDuration', parseInt(e.target.value))} />
              <div class="form-hint">Maximum time before the agent wraps up the call.</div>
            </div>
          </div>
          <div class="form-group">
            <div class="slider-group">
              <div class="slider-header">
                <label class="form-label">Ring Before Answer</label>
                <span class="slider-value">${(agent.answerDelay ?? 0) === 0 ? 'Instant' : `${agent.answerDelay} ring${agent.answerDelay > 1 ? 's' : ''}`}</span>
              </div>
              <input type="range" data-field="answerDelay" min="0" max="5" step="1" .value=${String(agent.answerDelay ?? 0)} @input=${(e) => this._sliderChanged('answerDelay', parseInt(e.target.value))} />
              <div class="form-hint">Number of rings before the AI answers. 0 = instant pickup.</div>
            </div>
          </div>
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Transfer Number</label>
            <input type="tel" class="form-input" data-field="transferNumber" .value=${agent.transferNumber} placeholder="+1 (555) 000-0000" />
            <div class="form-hint">Phone number for live agent transfer.</div>
          </div>
          <div class="form-group">
            <label class="form-label">Interruption Handling</label>
            <select class="form-select" data-field="interruptionHandling">
              <option value="allow" ?selected=${agent.interruptionHandling === 'allow'}>Allow Interruptions</option>
              <option value="queue" ?selected=${agent.interruptionHandling === 'queue'}>Queue Until Finished</option>
              <option value="ignore" ?selected=${agent.interruptionHandling === 'ignore'}>Ignore Interruptions</option>
            </select>
            <div class="form-hint">How the agent handles caller interruptions mid-speech.</div>
          </div>
        </div>
      </div>

      <div class="actions-footer">
        <button class="btn btn-danger-ghost" @click=${() => this.dispatchEvent(new CustomEvent('delete'))}>
          <i class="fa-sharp fa-light fa-trash"></i>
          Delete Agent
        </button>
        <div style="display:flex;gap:var(--space-3);">
          <button class="btn btn-secondary" @click=${() => this.dispatchEvent(new CustomEvent('back'))}>Cancel</button>
          <button class="btn btn-primary" ?disabled=${this.saving} @click=${() => this._collectAndSave()}>
            <i class="fa-sharp fa-light ${this.saving ? 'fa-spinner-third fa-spin' : 'fa-floppy-disk'}"></i>
            ${this.saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('agent-config-tab', AgentConfigTab);
